import React, { useEffect, useState } from "react";
import styled from "styled-components";
import uuid from "uuid";

import { SystemTag } from "@examspark/common";
import {
  assertNativeError,
  delayForIndexing,
} from "@examspark/common/dist/utils";
import {
  Button,
  Modal,
  ModalProps,
  SubmitButtons,
  Text,
  Toolbar,
  VSpace,
  useSelectedObjects,
} from "@examspark/common-ui";
import SearchField from "@examspark/common-ui/dist/components/inputs/SearchField";
import { ClassroomData } from "@examspark/types";

import {
  useMutationCreateClassroom,
  useMutationCreateUserClassroom,
} from "../../../api/operations/classroom";
import { useMutationCreateStudent } from "../../../api/operations/student";
import { useGoogleClassroom } from "../../../hooks/useGoogleClassroom";
import { GoogleConnectResponse } from "../../../hooks/useGoogleSignIn";
import { useAppContext } from "../../../providers/AppContext";
import GoogleConnect from "../google/GoogleConnect";

const Courses = styled(VSpace).attrs({ size: "0.5rem" })``;

const CourseItem = styled.div`
  padding: 1rem;
  border: solid 1px #ddd;
  border-radius: 0.5rem;
  display: flex;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border: solid 1px #ccc;
    background-color: #f2f2ff;
  }
`;
const CourseItemInput = styled.div``;
const CourseItemMeta = styled(VSpace).attrs({ size: "0.3rem" })``;
const CourseItemName = styled.div`
  font-weight: 700;
`;
const CourseItemDesc = styled.div`
  color: #666;
`;
const CourseItemTags = styled.div`
  display: flex;
  gap: 1rem;
  color: #888;
  font-size: 1.15rem;
`;

const GoogleLogin = styled.div`
  padding: 1rem;
  font-size: 1.3rem;
  color: #333;
  display: flex;
  gap: 1.5rem;
  flex-direction: column;
`;

const NewClassroomGoogle = (
  props: Omit<ModalProps, "isOpen"> & {
    onCreate: () => void;
    onBack: () => void;
  },
) => {
  const config = {
    tokenServerUrl:
      process.env.REACT_APP_REST_API_URL ?? "https://api.examspark.com",
  };

  const [state] = useAppContext();
  const gc = useGoogleClassroom(config);
  const [token, setToken] = useState<string>();
  const [googleTokens, setGoogleTokens] = useState<GoogleConnectResponse>();
  const [courses, setCourses] = useState<gapi.client.classroom.Course[]>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // @ts-expect-error id is possibly undefined in the google type
  const selectedCourses = useSelectedObjects<gapi.client.classroom.Course>([]);
  const [filter, setFilter] = useState("");

  const createClassroom = useMutationCreateClassroom();
  const createUserClassroom = useMutationCreateUserClassroom();
  const createStudent = useMutationCreateStudent();

  const fetchCourses = async () => {
    const result = await gc.fetchCourses();
    if (!result?.length) {
      alert(
        "Your Google Classroom account does not have any courses. Please create a course in Google Classroom and try again.",
      );
      return;
    }
    setCourses(result);
  };

  useEffect(() => {
    if (token) {
      gc.setToken(token);
      fetchCourses();
    }
    /** @todo gc hook causes infinite loops */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async () => {
    console.log("state", state);
    const allowRostering = state.app?.config?.allowRostering ?? false;

    try {
      setIsSubmitting(true);
      if (!state.user?.id) {
        console.error("No user id found");
        return;
      }
      if (!state.workspaceUser?.workspace?.id) {
        console.error("No workspace id found");
        return;
      }
      if (!selectedCourses.values.length) {
        throw new Error("Please select at least one course to import.");
      }
      if (!googleTokens?.refresh_token) {
        throw new Error("No refresh token found");
      }
      if (!googleTokens?.scope) {
        throw new Error("No scope token found");
      }

      // save courses
      await Promise.all(
        selectedCourses.values.map(async (course) => {
          if (!course.id) {
            console.error("No course id found");
            return;
          }
          if (!state.user?.id) {
            console.error("No user id found");
            return;
          }
          if (!state.workspaceUser?.workspace?.id) {
            console.error("No workspace id found");
            return;
          }

          const classroomId = uuid.v4();
          const data: ClassroomData = {
            platform: "GOOGLE_CLASSROOM",
            googleClassroom: {
              enabled: true,
              course: course,
              auth: {
                refreshToken: googleTokens.refresh_token,
                scope: googleTokens.scope,
              },
            },
          };

          const promises: any[] = [];
          promises.push(
            createClassroom.mutate({
              id: classroomId,
              sk: "classroom",
              name: course.name,
              sectionNumber: course.section ?? undefined,
              schemaVersion: "2020-01-01",
              data: JSON.stringify(data),
              creatorId: state.user.id,
              workspaceId: state.workspaceUser.workspace.id,
              tags: [
                {
                  key: SystemTag.UseCaseInsensitiveStudentIds,
                  type: "string",
                  value: "true",
                },
              ],
            }),
          );
          promises.push(
            createUserClassroom.mutate({
              userId: state.user.id,
              classroomId: classroomId,
              workspaceId: state.workspaceUser.workspace.id,
            }),
          );

          // get roster
          if (allowRostering) {
            const roster = await gc.fetchStudents(course.id);
            if (roster?.students) {
              for (const student of roster.students) {
                if (!student.profile?.id)
                  throw new Error("No student profile found");

                promises.push(
                  createStudent.mutateAsync({
                    classroomId,
                    studentId: student.profile.id,
                    service: "google-classroom",
                    input: {
                      id: classroomId,
                      idProviderType: "GOOGLE",
                      idProviderUserId: student.profile?.id ?? "",
                      firstName: student.profile?.name?.givenName ?? "",
                      lastName: student.profile?.name?.familyName ?? "",
                      fullName: student.profile?.name?.fullName ?? "",
                      photoURL: student.profile?.photoUrl ?? "",
                      studentId: student.profile?.id ?? "",
                      schemaVersion: "2020-01-01",
                      workspaceId: state.workspaceUser.workspace.id,
                    },
                  }),
                );
              }
            }
          }

          await Promise.all(promises);
        }),
      );

      await delayForIndexing();
      props.onCreate();
      setIsSubmitting(true);
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal.Body>
        <GoogleLogin>
          {(!token || !courses?.length) && (
            <>
              <GoogleConnect
                appKey={state.appLicense?.appKey}
                userId={state.user?.id}
                onConnect={(rsp) => {
                  setToken(rsp.access_token);
                  setGoogleTokens(rsp);
                }}
              >
                To import your Google Classroom, first sign in to Google. Then
                you’ll be prompted to select the courses you wish to import.
              </GoogleConnect>
            </>
          )}
          {courses !== undefined && (
            <>
              {courses?.length && (
                <div>
                  <VSpace>
                    <Text.Body>Select the Courses To Import:</Text.Body>
                    <Toolbar>
                      <Toolbar.Flex>
                        <SearchField
                          value={filter}
                          onChange={(value) => setFilter(value)}
                          placeholder="Filter Courses"
                        />
                      </Toolbar.Flex>
                      <Button title="Fetch Courses" onClick={fetchCourses}>
                        Reload Courses
                      </Button>
                    </Toolbar>
                    <Courses>
                      {courses.map((course) => {
                        if (!course.name?.toLowerCase().includes(filter))
                          return null;
                        return (
                          <CourseItem
                            key={course.id}
                            onClick={() => selectedCourses.toggle(course)}
                          >
                            <CourseItemInput>
                              <input
                                type="checkbox"
                                checked={selectedCourses.contains(course)}
                              />
                            </CourseItemInput>
                            <CourseItemMeta>
                              <CourseItemName>{course.name}</CourseItemName>
                              <CourseItemDesc>
                                {course.descriptionHeading}
                              </CourseItemDesc>
                              <CourseItemTags>
                                <div>Room: {course.room}</div>
                                <div>Section: {course.section}</div>
                                <div>ID: {course.id}</div>
                              </CourseItemTags>
                            </CourseItemMeta>
                          </CourseItem>
                        );
                      })}
                    </Courses>
                  </VSpace>
                </div>
              )}
            </>
          )}
          {/* <img alt="Google Login Screenshot" src={googleLoginImage} /> */}
        </GoogleLogin>
      </Modal.Body>
      <Modal.BasicFooter>
        <SubmitButtons
          isSubmitting={isSubmitting}
          onCancel={props.onBack}
          cancelLabel="Back"
          isSubmitDisabled={selectedCourses.values.length === 0}
          onSubmit={handleSubmit}
          submitLabel="Import Classrooms"
        />
      </Modal.BasicFooter>
    </>
  );
};

export default NewClassroomGoogle;
